(() => {
  if (window.inmicaReady) return;
  window.inmicaReady = true;
  const root = window.Shopify?.routes?.root || "/";
  const cartUrl = `${root}cart`;
  const route = (path) => `${root}cart/${path}.js`;
  const quantity = (value) => Math.min(99, Math.max(1, Math.round(Number(value) || 1)));
  const normalize = (value) =>
    String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  let cart = null;
  let mutationPending = false;
  let syncPending = null;
  let syncAfterMutation = false;
  let dialog;
  let dialogReturnFocus;
  let previousOverflow = "";
  let pendingCartFocus;
  // This version identifies a UX decision, not a theme release. Never rotate it
  // on deploy or clear it when the cart is emptied.
  const confirmationKey = "inmica:cart-confirmation:seen:v1";
  let confirmationSeen = false;
  let confirmationStorageAvailable = true;
  let addStatus;
  let addStatusTimer;
  function readConfirmation() {
    try {
      confirmationSeen = localStorage.getItem(confirmationKey) === "1";
    } catch {
      confirmationStorageAvailable = false;
    }
    return confirmationSeen;
  }
  readConfirmation();
  window.addEventListener("storage", (event) => {
    if (event.key === confirmationKey || event.key === null) readConfirmation();
  });
  function initAddStatus() {
    if (addStatus?.isConnected) return;
    addStatus = element("div", "inmica-cart-added-status");
    addStatus.dataset.inmicaAddStatus = "";
    addStatus.setAttribute("role", "status");
    addStatus.setAttribute("aria-live", "polite");
    addStatus.setAttribute("aria-atomic", "true");
    document.body.append(addStatus);
  }
  function announceAdded() {
    initAddStatus();
    clearTimeout(addStatusTimer);
    addStatus.textContent = "";
    requestAnimationFrame(() => {
      addStatus.textContent = "Producto agregado al carrito";
      addStatus.classList.add("is-visible");
      addStatusTimer = setTimeout(() => addStatus.classList.remove("is-visible"), 5000);
    });
  }
  async function confirmAdded(added, currentCart, trigger, addedQuantity) {
    announceAdded();
    if (!confirmationStorageAvailable || readConfirmation()) return;
    // Web Locks serializes the read/write across tabs of this origin. If the
    // browser cannot coordinate them, prefer a non-interrupting status.
    if (!navigator.locks?.request || typeof HTMLDialogElement === "undefined") return;
    try {
      await navigator.locks.request(confirmationKey, () => {
        if (!confirmationStorageAvailable || readConfirmation()) return;
        if (typeof HTMLDialogElement.prototype.showModal !== "function") return;
        // A blocked write must degrade before opening any modal. The server has
        // already confirmed this addition; no failed POST reaches this point.
        localStorage.setItem(confirmationKey, "1");
        confirmationSeen = true;
        showAdded(added, currentCart, trigger, addedQuantity);
      });
    } catch {
      confirmationStorageAvailable = false;
      // The cart remains authoritative even if storage/coordination fails.
    }
  }
  const temporarilyDisabled = new Set();
  const format = (cents) => {
    const currency = cart?.currency || window.Shopify?.currency?.active || "CLP";
    const formatter = new Intl.NumberFormat(currency === "CLP" ? "es-CL" : document.documentElement.lang || "es-CL", {
      style: "currency",
      currency,
      currencyDisplay: "code",
      maximumFractionDigits: currency === "CLP" ? 0 : 2,
    });
    // Browsers can label pesos as either "$" or "CLP". Build the label once,
    // independently of the currency token, to match the server-rendered prices.
    const amount = formatter
      .formatToParts(Number(cents || 0) / 100)
      .filter((part) => part.type !== "currency" && part.type !== "literal")
      .map((part) => part.value)
      .join("");
    return `${currency === "CLP" ? "$" : ""}${amount} ${currency}`;
  };
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }
  async function requestJson(url, payload) {
    const response = await fetch(url, {
      method: payload === undefined ? "GET" : "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json", ...(payload === undefined ? {} : { "Content-Type": "application/json" }) },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    });
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error("La tienda no pudo responder. Revisa tu carrito antes de intentarlo nuevamente.");
    }
    if (!response.ok) {
      const detail =
        typeof data.description === "string"
          ? data.description
          : typeof data.message === "string"
            ? data.message
            : "Inténtalo nuevamente.";
      const error = new Error(detail);
      error.status = response.status;
      throw error;
    }
    return data;
  }
  function updateCount() {
    if (!cart) return;
    document.querySelectorAll("[data-inmica-cart-count]").forEach((node) => {
      node.textContent = String(cart.item_count);
      node.setAttribute(
        "aria-label",
        `${cart.item_count} ${cart.item_count === 1 ? "producto" : "productos"} en el carrito`,
      );
    });
    document.dispatchEvent(new CustomEvent("inmica:cart-updated", { detail: { cart } }));
  }
  function focusedCartControl(page) {
    const focused = document.activeElement;
    if (!page.contains(focused)) return null;
    return {
      key: focused.closest("[data-inmica-line-key]")?.dataset.inmicaLineKey,
      action: focused.dataset.inmicaCartAction,
    };
  }
  function restoreCartFocus(page, focus) {
    if (!focus?.action) return;
    const row = [...page.querySelectorAll("[data-inmica-line-key]")].find(
      (node) => node.dataset.inmicaLineKey === focus.key,
    );
    const target =
      row?.querySelector(`[data-inmica-cart-action="${focus.action}"]`) ||
      page.querySelector("[data-inmica-cart-heading]");
    target?.focus({ preventScroll: true });
  }
  async function renderCartPage() {
    const pages = [...document.querySelectorAll("[data-inmica-cart-page]")];
    for (const page of pages) {
      const sectionId = page.dataset.sectionId;
      if (!sectionId) continue;
      const focus = focusedCartControl(page);
      const response = await fetch(`${cartUrl}?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok)
        throw new Error(
          "Tu carrito se guardó, pero no pudimos actualizar esta vista. Recarga la página para revisarlo.",
        );
      const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
      const replacement = parsed.querySelector("[data-inmica-cart-page]");
      if (!replacement) throw new Error("Tu carrito se guardó. Recarga esta página para ver su estado actualizado.");
      page.replaceWith(replacement);
      if (mutationPending) setBusy(true);
      if (mutationPending) pendingCartFocus = { page: replacement, focus };
      else restoreCartFocus(replacement, focus);
    }
  }
  function syncCart() {
    if (mutationPending) {
      syncAfterMutation = true;
      return Promise.resolve(cart);
    }
    if (syncPending) return syncPending;
    syncPending = (async () => {
      cart = await requestJson(`${cartUrl}.js`);
      updateCount();
      await renderCartPage();
      return cart;
    })().finally(() => {
      syncPending = null;
    });
    return syncPending;
  }
  function setBusy(busy) {
    document
      .querySelectorAll(
        "[data-inmica-add-cart], [data-inmica-cart-action], [data-inmica-cart-update], [data-inmica-variant], [data-inmica-checkout]",
      )
      .forEach((node) => {
        if (
          !(node instanceof HTMLButtonElement || node instanceof HTMLInputElement || node instanceof HTMLSelectElement)
        )
          return;
        if (busy && !node.disabled) {
          temporarilyDisabled.add(node);
          node.disabled = true;
        }
        if (!busy && temporarilyDisabled.has(node)) {
          node.disabled = false;
          temporarilyDisabled.delete(node);
        }
      });
    document
      .querySelectorAll("[data-inmica-cart-page], [data-inmica-cart-form]")
      .forEach((node) => node.setAttribute("aria-busy", String(busy)));
    if (!busy) {
      temporarilyDisabled.clear();
      if (pendingCartFocus) {
        restoreCartFocus(pendingCartFocus.page, pendingCartFocus.focus);
        pendingCartFocus = null;
      }
    }
  }
  function clearErrors() {
    document.querySelectorAll("[data-inmica-cart-error]").forEach((node) => {
      node.hidden = true;
      node.textContent = "";
    });
  }
  function showError(message, trigger) {
    const scope = trigger?.closest("[data-inmica-cart-form], [data-inmica-cart-page]");
    let notice = scope?.querySelector("[data-inmica-cart-error]");
    if (!notice) {
      notice = document.querySelector("[data-inmica-global-cart-error]");
      if (!notice) {
        notice = element("div", "inmica-cart-error inmica-cart-error--floating");
        notice.dataset.inmicaGlobalCartError = "";
        notice.dataset.inmicaCartError = "";
        document.body.append(notice);
      }
    }
    notice.setAttribute("role", "alert");
    notice.textContent = message;
    notice.hidden = false;
  }
  function showAdded(added, currentCart, trigger, addedQuantity) {
    if (!dialog) {
      dialog = element("dialog", "inmica-cart-modal");
      dialog.setAttribute("aria-labelledby", "InmicaCartModalTitle");
      dialog.setAttribute("aria-describedby", "InmicaCartModalQuestion");
      dialog.innerHTML = `
        <div class="inmica-cart-modal__success"><svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m7 12 3 3 7-7"/></svg> Producto agregado al carrito</div>
        <button type="button" class="inmica-cart-modal__close" data-inmica-cart-close aria-label="Cerrar y seguir comprando">×</button>
        <div class="inmica-cart-modal__body">
          <h2 id="InmicaCartModalTitle">¡Ya está en tu carrito!</h2>
          <div class="inmica-cart-modal__product"><img data-inmica-added-image alt="" width="112" height="112"><div><h3 data-inmica-added-title></h3><p data-inmica-added-variant></p><p data-inmica-added-quantity></p><strong data-inmica-added-price></strong></div></div>
          <div class="inmica-cart-modal__subtotal"><span data-inmica-added-count></span><strong data-inmica-added-total></strong></div>
          <p id="InmicaCartModalQuestion" class="inmica-cart-modal__question">El producto se agregó a tu carrito. ¿Quieres seguir comprando o revisar tu pedido?</p>
          <div class="inmica-cart-modal__actions"><button type="button" class="inmica-button inmica-button--outline" data-inmica-cart-continue>Seguir comprando</button><a class="inmica-button inmica-button--red" data-inmica-cart-link>Ver carrito <span aria-hidden="true">→</span></a></div>
          <p class="inmica-cart-launch-notice" data-inmica-modal-launch-notice><strong>Compras disponibles próximamente</strong><span>Pronto podrás finalizar tu compra. Por ahora puedes agregar productos y preparar tu carrito.</span></p>
        </div>`;
      dialog.querySelector("[data-inmica-cart-link]").href = cartUrl;
      dialog
        .querySelectorAll("[data-inmica-cart-close], [data-inmica-cart-continue]")
        .forEach((button) => button.addEventListener("click", () => dialog.close()));
      dialog.addEventListener("click", (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          dialog.close();
      });
      dialog.addEventListener("close", () => {
        document.body.style.overflow = previousOverflow;
        document.documentElement.classList.remove("inmica-cart-modal-open");
        if (dialogReturnFocus?.isConnected) dialogReturnFocus.focus({ preventScroll: true });
      });
      dialog.addEventListener("keydown", (event) => {
        if (event.key !== "Tab") return;
        const controls = [...dialog.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')].filter(
          (node) => node.getClientRects().length,
        );
        const first = controls[0];
        const last = controls.at(-1);
        if (!first) return;
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      });
      document.body.append(dialog);
    }
    const item =
      currentCart.items.find((entry) => entry.key === added.key) ||
      currentCart.items.find((entry) => String(entry.variant_id) === String(added.variant_id || added.id)) ||
      added;
    dialog.querySelector("[data-inmica-added-title]").textContent = item.product_title || item.title;
    const variant = dialog.querySelector("[data-inmica-added-variant]");
    variant.textContent = item.variant_title === "Default Title" ? "" : item.variant_title || "";
    variant.hidden = !variant.textContent;
    dialog.querySelector("[data-inmica-added-quantity]").textContent =
      `Agregaste ${addedQuantity} ${addedQuantity === 1 ? "unidad" : "unidades"} · Cantidad en el carrito: ${item.quantity}`;
    dialog.querySelector("[data-inmica-added-price]").textContent = format(
      item.final_line_price ?? item.line_price ?? item.price * item.quantity,
    );
    dialog.querySelector("[data-inmica-added-count]").textContent =
      `Subtotal (${currentCart.item_count} ${currentCart.item_count === 1 ? "producto" : "productos"})`;
    dialog.querySelector("[data-inmica-added-total]").textContent = format(currentCart.total_price);
    const photo = dialog.querySelector("[data-inmica-added-image]");
    const source = item.featured_image?.url || item.image;
    photo.hidden = !source;
    if (source) {
      photo.src = source;
      photo.alt = item.product_title || item.title;
    }
    dialog.querySelector("[data-inmica-modal-launch-notice]").hidden =
      window.inmicaCartSettings?.checkoutDisabled === false;
    dialogReturnFocus = trigger;
    if (typeof dialog.showModal !== "function") {
      return;
    }
    if (!dialog.open) {
      previousOverflow = document.body.style.overflow;
      dialog.showModal();
    }
    document.body.style.overflow = "hidden";
    document.documentElement.classList.add("inmica-cart-modal-open");
    dialog.querySelector("[data-inmica-cart-continue]").focus();
  }
  async function mutateCart(trigger, path, payload, addedQuantity) {
    if (mutationPending) return;
    mutationPending = true;
    const focusedTrigger = document.activeElement === trigger;
    clearErrors();
    setBusy(true);
    const label = trigger?.querySelector("[data-inmica-add-label]");
    const originalLabel = label?.textContent;
    if (label) label.textContent = "Agregando…";
    let result;
    let succeeded = false;
    try {
      // Finish any older read before writing, so a stale response cannot overwrite a mutation.
      if (syncPending) await syncPending.catch(() => {});
      const writeAndRead = async () => {
        result = await requestJson(route(path), payload);
        succeeded = true;
        cart = path === "add" ? await requestJson(`${cartUrl}.js`) : result;
        updateCount();
        await renderCartPage();
        if (path !== "add") {
          const status = document.querySelector("[data-inmica-cart-status]");
          if (status) status.textContent = cart.item_count ? "Carrito actualizado." : "Tu carrito está vacío.";
        }
      };
      // Also serialize Shopify writes across tabs, including the first POST
      // that establishes a cart cookie. Never repeat an uncertain mutation.
      if (navigator.locks?.request) await navigator.locks.request("inmica:cart-mutation:v1", writeAndRead);
      else await writeAndRead();
    } catch (error) {
      const message =
        error.status === 422
          ? `No pudimos actualizar el carrito: ${error.message}`
          : succeeded
            ? "El cambio se guardó, pero no pudimos actualizar la vista. Recarga la página para revisar tu carrito."
            : "No pudimos confirmar el cambio. Revisa tu carrito antes de intentarlo nuevamente.";
      // A failed POST may still have reached Shopify; never retry it automatically.
      try {
        cart = await requestJson(`${cartUrl}.js`);
        updateCount();
        await renderCartPage();
      } catch {
        /* Preserve the last confirmed state. */
      }
      showError(message, trigger?.isConnected ? trigger : document.querySelector("[data-inmica-cart-page]"));
      result = null;
    } finally {
      mutationPending = false;
      setBusy(false);
      if (label) label.textContent = originalLabel;
      // Disabling the focused button can move focus to body. Restore it only
      // when the buyer has not moved to another control while waiting.
      if (focusedTrigger && trigger?.isConnected && document.activeElement === document.body)
        trigger.focus({ preventScroll: true });
    }
    if (result && path === "add") await confirmAdded(result.items?.[0] || result, cart, trigger, addedQuantity);
    if (syncAfterMutation) {
      syncAfterMutation = false;
      syncCart().catch(() => {});
    }
  }
  function addToCart(button) {
    if (mutationPending || button.disabled) return;
    const variantId = button.form?.querySelector('[name="id"]')?.value || button.dataset.variantId;
    if (!/^\d+$/.test(String(variantId))) {
      showError("Selecciona una presentación del producto para continuar.", button);
      return;
    }
    const product = button.closest("[data-inmica-product]");
    const qty = quantity(
      product?.querySelector("[data-inmica-product-quantity]")?.value ||
        button.form?.querySelector('[name="quantity"]')?.value ||
        1,
    );
    mutateCart(button, "add", { items: [{ id: variantId, quantity: qty }] }, qty);
  }
  document.addEventListener("submit", (event) => {
    if (event.target.matches("[data-inmica-cart-checkout-form]") && mutationPending) {
      // Keep the native Shopify checkout transport, but never submit disabled
      // quantity inputs or an older cart while its latest change is pending.
      event.preventDefault();
      const status = event.target.closest("[data-inmica-cart-page]")?.querySelector("[data-inmica-cart-status]");
      if (status) status.textContent = "Estamos actualizando tu carrito. Espera un momento para continuar.";
      return;
    }
    if (event.target.matches("[data-inmica-cart-form]")) {
      event.preventDefault();
      const button = event.submitter?.matches("[data-inmica-add-cart]")
        ? event.submitter
        : event.target.querySelector("[data-inmica-add-cart]");
      if (button) addToCart(button);
    }
  });
  document.addEventListener("change", (event) => {
    if (!event.target.matches('[data-inmica-cart-action="quantity"]')) return;
    const input = event.target;
    const key = input.closest("[data-inmica-line-key]")?.dataset.inmicaLineKey;
    const value = Math.min(99, Math.max(0, Math.round(Number(input.value) || 0)));
    if (key) mutateCart(input, "change", { id: key, quantity: value });
  });
  document.addEventListener("click", (event) => {
    const add = event.target.closest("[data-inmica-add-cart]");
    if (add && !add.form) {
      event.preventDefault();
      addToCart(add);
    }
    const action = event.target.closest("[data-inmica-cart-action]");
    if (action && action.dataset.inmicaCartAction !== "quantity") {
      event.preventDefault();
      if (mutationPending) return;
      const kind = action.dataset.inmicaCartAction;
      if (kind === "clear") mutateCart(action, "clear", {});
      else {
        const row = action.closest("[data-inmica-line-key]");
        const key = row?.dataset.inmicaLineKey;
        const input = row?.querySelector('[data-inmica-cart-action="quantity"]');
        const next =
          kind === "remove" ? 0 : Math.min(99, Math.max(0, Number(input?.value || 1) + (kind === "increase" ? 1 : -1)));
        if (key) mutateCart(action, "change", { id: key, quantity: next });
      }
    }
  });
  window.addEventListener("pageshow", () => syncCart().catch(() => {}));
  window.addEventListener("focus", () => syncCart().catch(() => {}));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) syncCart().catch(() => {});
  });
  function filterCatalog(catalog) {
    const query = normalize(catalog.querySelector("[data-inmica-filter-query]")?.value || "");
    const minValue = catalog.querySelector("[data-inmica-filter-min]")?.value;
    const maxValue = catalog.querySelector("[data-inmica-filter-max]")?.value;
    const minimum = minValue === "" ? 0 : Math.max(0, Number(minValue) || 0);
    const maximum = maxValue === "" ? Infinity : Math.max(0, Number(maxValue) || 0);
    const types = [...catalog.querySelectorAll("[data-inmica-filter-type]:checked")].map((node) => node.value);
    let count = 0;
    catalog.querySelectorAll("[data-inmica-card]").forEach((card) => {
      const matches =
        normalize(card.dataset.productTitle).includes(query) &&
        Number(card.dataset.price) >= minimum &&
        Number(card.dataset.price) <= maximum &&
        (!types.length || types.includes(card.dataset.productType));
      card.hidden = !matches;
      if (matches) count++;
    });
    catalog.querySelector("[data-inmica-result-count]").textContent = count;
    catalog.querySelector("[data-inmica-filter-empty]").hidden = count > 0;
  }
  let lightbox;
  function openImage(button) {
    const image = button.querySelector("img");
    if (!image) return;
    if (!lightbox) {
      lightbox = element("dialog", "inmica-lightbox");
      lightbox.setAttribute("aria-label", "Imagen ampliada del producto");
      const close = element("button", "", "×");
      close.type = "button";
      close.setAttribute("aria-label", "Cerrar imagen ampliada");
      close.addEventListener("click", () => lightbox.close());
      lightbox.append(close, element("img"));
      document.body.append(lightbox);
      lightbox.addEventListener("click", (event) => {
        if (event.target === lightbox) lightbox.close();
      });
      lightbox.addEventListener("close", () => {
        document.body.style.overflow = "";
      });
    }
    const expanded = lightbox.querySelector("img");
    expanded.src = image.currentSrc || image.src;
    expanded.alt = image.alt;
    if (typeof lightbox.showModal === "function") {
      lightbox.showModal();
      document.body.style.overflow = "hidden";
    } else {
      window.open(expanded.src, "_blank", "noopener");
    }
  }
  document.addEventListener("click", (event) => {
    const step = event.target.closest("[data-inmica-quantity-step]");
    if (step) {
      const input = step.closest(".inmica-quantity")?.querySelector("input");
      if (input) {
        input.value = quantity(Number(input.value) + Number(step.dataset.inmicaQuantityStep));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
    const reset = event.target.closest("[data-inmica-filter-reset]");
    if (reset) {
      const catalog = reset.closest("[data-inmica-catalog]");
      catalog
        .querySelectorAll("[data-inmica-filter-query], [data-inmica-filter-min], [data-inmica-filter-max]")
        .forEach((node) => {
          node.value = "";
        });
      catalog.querySelectorAll("[data-inmica-filter-type]").forEach((node) => {
        node.checked = false;
      });
      filterCatalog(catalog);
    }
    const thumb = event.target.closest("[data-inmica-thumbnail]");
    if (thumb) {
      const gallery = thumb.closest("[data-inmica-gallery]");
      const image = gallery.querySelector(".inmica-product-gallery__photo");
      image.removeAttribute("srcset");
      image.src = thumb.dataset.imageSrc;
      image.alt = thumb.dataset.imageAlt;
      gallery
        .querySelectorAll("[data-inmica-thumbnail]")
        .forEach((node) => node.setAttribute("aria-pressed", String(node === thumb)));
    }
    const zoom = event.target.closest("[data-inmica-zoom]");
    if (zoom) openImage(zoom);
  });
  document.addEventListener("input", (event) => {
    if (event.target.matches("[data-inmica-filter-query], [data-inmica-filter-min], [data-inmica-filter-max]"))
      filterCatalog(event.target.closest("[data-inmica-catalog]"));
  });
  document.addEventListener("change", (event) => {
    const target = event.target;
    if (target.matches("[data-inmica-filter-type]")) filterCatalog(target.closest("[data-inmica-catalog]"));
    if (target.matches("[data-inmica-sort]")) target.form?.requestSubmit();
    if (target.matches("[data-inmica-product-quantity]")) target.value = quantity(target.value);
    if (target.matches("[data-inmica-variant]")) {
      const product = target.closest("[data-inmica-product]");
      const option = target.selectedOptions[0];
      const button = product.querySelector("[data-inmica-add-cart]");
      if (!button) return;
      button.dataset.variantId = option.value;
      const variantInput = button.form?.querySelector('[name="id"]');
      if (variantInput) variantInput.value = option.value;
      button.dataset.price = option.dataset.price;
      button.dataset.variantTitle = option.dataset.title;
      button.disabled = option.dataset.available === "false";
      const label = button.querySelector("[data-inmica-add-label]");
      if (label) label.textContent = button.disabled ? "Agotado" : "Agregar al carrito";
      product.querySelector("[data-inmica-product-price]").textContent = format(Number(option.dataset.price));
    }
  });
  let revealObserver;
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  function init() {
    initAddStatus();
    syncCart().catch(() => {
      /* Keep the server-rendered cart if the connection is unavailable. */
    });
    if (motion.matches || !("IntersectionObserver" in window)) return;
    document.documentElement.classList.add("inmica-reveal-ready");
    revealObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 },
    );
    document.querySelectorAll("[data-inmica-reveal]:not(.is-visible)").forEach((node) => revealObserver.observe(node));
  }
  motion.addEventListener("change", () => {
    if (motion.matches) {
      revealObserver?.disconnect();
      document.querySelectorAll("[data-inmica-reveal]").forEach((node) => node.classList.add("is-visible"));
    }
  });
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const opened = [...document.querySelectorAll(".inmica-nav-dropdown[open], .inmica-mobile-menu[open]")];
    opened.forEach((menu) => {
      menu.open = false;
    });
    if (opened.length) opened[0].querySelector("summary")?.focus({ preventScroll: true });
  });
  document.addEventListener("click", (event) => {
    document.querySelectorAll(".inmica-nav-dropdown[open], .inmica-mobile-menu[open]").forEach((menu) => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });
  document.addEventListener(
    "toggle",
    (event) => {
      if (!event.target.matches?.(".inmica-nav-dropdown[open]")) return;
      document.querySelectorAll(".inmica-nav-dropdown[open]").forEach((menu) => {
        if (menu !== event.target) menu.open = false;
      });
    },
    true,
  );
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();
